// src/components/league/LeagueCard.jsx

import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Stack,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import Groups from '@mui/icons-material/Groups';
import SportsIcon from '@mui/icons-material/Sports';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const formatLeagueDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

const LeagueCard = ({ 
  league, 
  orgId, 
  isPublic = false, 
  onDelete,
  onNavigate 
}) => {
  const navigate = useNavigate();
  
  const handleCardClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onNavigate) {
      onNavigate(league);
    } else if (isPublic && league.slug) {
      // Default public navigation
      navigate(`/leagues/${league.slug}/standings`);
    } else if (!isPublic && orgId && league.id) {
      // Default private navigation
      navigate(`/organization/${orgId}/league/${league.id}`);
    }
  };

  const handleChipClick = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isPublic && league.slug) {
      // Public navigation
      if (type === 'teams') {
        navigate(`/leagues/${league.slug}/teams`);
      } else if (type === 'games') {
        navigate(`/leagues/${league.slug}/schedule`);
      }
    } else if (!isPublic && orgId && league.id) {
      // Private navigation
      if (type === 'teams') {
        navigate(`/organization/${orgId}/league/${league.id}`);
      } else if (type === 'games') {
        navigate(`/organization/${orgId}/league/${league.id}/schedule`);
      }
    }
  };

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4
        }
      }}
    >
      <CardContent 
        sx={{ 
          flexGrow: 1,
          cursor: 'pointer',
          p: 3
        }}
        onClick={handleCardClick}
      >
        <Typography 
          variant="h6" 
          component="h2" 
          gutterBottom
          sx={{ 
            color: '#2E4156',
            fontWeight: 'bold',
            mb: 2,
            ...(isPublic && {
              '&:hover': {
                color: 'primary.dark',
                textDecoration: 'underline'
              }
            })
          }}
        >
          {league.name}
        </Typography>
        
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
          <Chip 
            icon={<Groups />}
            label={`${league.teamCount || 0} Teams`}
            size="small"
            variant="outlined"
            sx={{ 
              color: '#2E4156', 
              borderColor: '#2E4156',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: '#2E4156',
                color: 'white'
              }
            }}
            onClick={(e) => handleChipClick(e, 'teams')}
            clickable={true}
          />
          <Chip 
            icon={<SportsIcon />}
            label={`${league.gameCount || 0} Games`}
            size="small"
            variant="outlined"
            sx={{ 
              color: '#2E4156', 
              borderColor: '#2E4156',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: '#2E4156',
                color: 'white'
              }
            }}
            onClick={(e) => handleChipClick(e, 'games')}
            clickable={true}
          />
        </Stack>

        {(league.startDate || league.endDate) && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 2 }}>
            <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {formatLeagueDate(league.startDate)}{league.startDate && league.endDate ? ' – ' : ''}{formatLeagueDate(league.endDate)}
            </Typography>
          </Stack>
        )}
        
        {league.description && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {league.description}
          </Typography>
        )}
      </CardContent>
      
      {!isPublic && onDelete && (
        <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
          <IconButton 
            onClick={(e) => {
              e.stopPropagation();
              onDelete(league);
            }}
            size="small"
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon />
          </IconButton>
        </CardActions>
      )}
    </Card>
  );
};

export default LeagueCard;